import React, { useCallback, useRef, useMemo, KeyboardEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { OrderFieldSpaceNames } from 'constants/order'
import { OrderFields, LogisticType } from 'types/order'
import { selectOrderFormErrors, selectOrderWrite } from 'store/order/order.selectors'
import { changeOrderHandler } from 'store/order/order.actions'

import classes from './style.module.css'

interface RelatedFieldProps {
  fieldName: string
  fieldToCopy: string
  relatedFields?: { [key: string]: string }
}

const FIRST_FACE_FIELDS: string[] = [
  OrderFields.LOGISTIC_FIRST_FACE,
  OrderFields.LOGISTIC_FIRST_FACE_BITH,
  OrderFields.CARRIER_FIRST_FACE_PHONE_1,
  OrderFields.CARRIER_FIRST_FACE_PHONE_2,
]

const getSuffixLabel = (fieldToCopy: string, logisticType: LogisticType | undefined): string => {
  if (FIRST_FACE_FIELDS.includes(fieldToCopy)) {
    switch (logisticType) {
      case LogisticType.LEGAL:
      case LogisticType.INDIVIDUAL:
        return 'Ген. дир.'
      case LogisticType.SELF_EMPLOYED:
        return 'самозанятый'
      default:
        return '1-е лицо'
    }
  }
  return OrderFieldSpaceNames[fieldToCopy] || ''
}

const RelatedField: React.FC<RelatedFieldProps> = ({ fieldName, fieldToCopy, relatedFields = {} }) => {
  const dispatch = useDispatch()
  const form: any = useSelector(selectOrderWrite)
  const errors: any = useSelector(selectOrderFormErrors)
  const buttonRef = useRef<HTMLDivElement>(null)

  const logisticType = form?.[OrderFields.LOGISTIC_TYPE]

  const suffixLabel = useMemo(() => getSuffixLabel(fieldToCopy, logisticType), [fieldToCopy, logisticType])

  const onClick = useCallback(() => {
    const relatedFieldsData = Object.entries(relatedFields).reduce((acc, [key, fallbackKey]) => {
      acc[key] = form[key] || form[fallbackKey]
      return acc
    }, {} as Record<string, string | boolean | number>)

    dispatch(changeOrderHandler({ [fieldName]: form[fieldToCopy], ...relatedFieldsData }))
  }, [dispatch, form, fieldToCopy, fieldName, relatedFields])

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  if (form[fieldName] || !form[fieldToCopy] || errors[fieldToCopy]?.isValid === false) {
    return null
  }

  return (
    <div
      className={classes.container}
      onMouseDown={(e) => {
        e.preventDefault()
        if (buttonRef?.current) buttonRef.current.focus()
        return false
      }}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      ref={buttonRef}
    >
      <span>Вставить&nbsp;</span>
      <span className="text-primary">{form[fieldToCopy]}</span>
      <span>&nbsp;({suffixLabel})</span>
    </div>
  )
}

export default RelatedField
